/**
 * VEC-3317 — declaración de los filtros de la grilla de Solicitudes de Stock para
 * <GridFilterPanel />.
 *
 * Módulo aparte porque lo consumen DOS lados: `InventarioSolicitudesGridFiltros` (para renderizar) e
 * `InventarioSolicitudesGrid` (para saber cuándo el query vigente difiere del vacío, que es lo que
 * decide si se ve la franja de resultados).
 *
 * DOS CLAVES DEL QUERY NO SON CAMPOS DEL PANEL:
 *  - `vista`, que la maneja la pestaña (Pendientes / Finalizadas) y no el panel: es la pantalla en
 *    la que estás, no un filtro que se acumula con los demás.
 *  - `ubicacionType`, que agrega la grilla cuando hay depósitos elegidos. Mismo motivo que en las
 *    otras dos pantallas del módulo: la columna de la base es polimórfica, el par (tipo, ids) viaja
 *    completo o no viaja, y el tipo no es una decisión del usuario.
 */

export const INVENTARIO_SOLICITUDES_FILTROS_STORAGE_KEY = 'InventarioSolicitudesGrid_filtros';

/**
 * ESTA GRILLA NO TIENE FILTRO POR DEFECTO, a diferencia del historial de movimientos (que abre con
 * los últimos 30 días).
 *
 * Es deliberado y coincide con el backend: una solicitud pendiente de hace tres meses sigue siendo
 * trabajo por hacer, y esconderla detrás de una ventana de fechas sería esconder trabajo. Lo que
 * acota esta pantalla es la pestaña, no el tiempo.
 *
 * Se exporta igual —vacío— para que la grilla y el panel hablen del "default" en un solo lugar: si
 * algún día se decide abrir con un período, se declara acá y las dos partes lo respetan solas.
 */
export const getInventarioSolicitudesFilterDefaults = () => ({});

const ANCHO_MEDIO = { xs: 12, sm: 6, md: 3, lg: 3 };

export const buildInventarioSolicitudesFilterFields = () => [
	{
		// El período NO viaja con su propio nombre: se serializa a las dos claves del rango. La
		// opción elegida es UI; el backend recibe fechas. Sin período elegido no manda ninguna, y el
		// backend tampoco aplica una ventana por defecto.
		name: 'periodo',
		type: 'periodo',
		startName: 'fechaDesde',
		endName: 'fechaHasta',
		messageId: 'inventarioSolicitudes.grid.filtros_periodo',
		label: 'Fecha de solicitud',
		size: ANCHO_MEDIO,
	},
	{
		// Múltiple, igual que en el historial de movimientos: cada fila es un pedido a UN depósito,
		// así que sumar depósitos une listas de trabajo en vez de sumar existencias. El endpoint lo
		// acepta como lista (ItemStockSolicitudValidation::listaDeIds).
		name: 'ubicacionId',
		type: 'multiselect',
		messageId: 'inventarioSolicitudes.grid.filtros_deposito',
		label: 'Depósito',
		size: ANCHO_MEDIO,
	},
	{
		// Texto libre y no un desplegable de ítems: `/items/simple-search` hace un `get()` SIN limit
		// y con miles de ítems escanea la tabla entera, así que no se lo puede usar para alimentar un
		// filtro. El backend resuelve este texto con un LIKE sobre número de parte y descripción.
		// Cuando ese endpoint se arregle, esto pasa a ser un `pastableMultiselect` y el query ya
		// soporta las dos mitades (`item` + `itemBusqueda`) desde el arranque.
		name: 'itemBusqueda',
		type: 'text',
		messageId: 'inventarioSolicitudes.grid.filtros_item',
		label: 'Repuesto',
		size: ANCHO_MEDIO,
	},
	{
		// Número de ticket. Es texto porque el panel no tiene tipo numérico, así que la grilla
		// descarta lo no numérico antes de armar el query: el endpoint valida entero positivo y un
		// "T-123" devolvería 400 en vez de una búsqueda.
		name: 'ticket',
		type: 'text',
		messageId: 'inventarioSolicitudes.grid.filtros_ticket',
		label: 'N° de ticket',
		size: ANCHO_MEDIO,
	},
	{
		// El id de la fila, EXACTO: es para ir a una línea concreta que alguien nombró (un reporte,
		// un mensaje, la lupa de otra pantalla), no para explorar. Por eso no hace LIKE —buscar "4"
		// traería la 4, la 14 y la 42— y devuelve una fila o ninguna.
		//
		// Texto por lo mismo que el ticket: el panel no tiene tipo numérico y la grilla descarta lo
		// no numérico antes de armar el query, porque el endpoint valida entero positivo.
		name: 'id',
		type: 'text',
		messageId: 'inventarioSolicitudes.grid.filtros_id',
		label: 'ID de solicitud',
		size: ANCHO_MEDIO,
	},
];
